import type { ToolDefinition, ToolType } from "../types";

const IMAGE_COLOR_EFFECTS_TOOL_TYPE = {
  label: "Professional Colour Grading",
  icon: "Palette",
} as const satisfies ToolType;

const imageColorEffectsTool: ToolDefinition = {
  slug: "image-color-effects",
  name: "Free Photo Filter Online | Adjust Color, Brightness & Grayscale",
  title: "Image Color Effects & Filters",
  description:
    "Apply professional photo filters online for free. Adjust image brightness, contrast, convert to grayscale, or add vintage cinematic effects with no watermark.",
  longDescription:
    "Transform your photos with our free online photo filter app. Easily make an image black and white, apply a cinematic vintage effect, or manually adjust brightness, contrast, and saturation. Edit your images directly in your browser with high-quality output, zero watermarks, and no account required.",
  toolType: {
    label: "Color Grading Editor",
    icon: "SlidersHorizontal",
  },
  heroBadges: [
    "100% Free",
    "No Watermark",
    "No Account Required",
    "Live Preview",
    "Cinematic Presets",
  ],
  category: "Editing Tools",
  categorySlug: "image-editing-tools",
  href: "/tools/image-color-effects",
  api: "/api/v1/image-color-effect",
  keywords: [
    "snappy photo filter app",
    "convert color picture to grayscale",
    "adjust image brightness online",
    "how to make photos cinematic",
    "change png hue easily",
    "free vintage styling editor",
    "black and white image maker",
    "lighting and contrast analyzer",
    "recolor jpeg online free",
    "picture tone checker software",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Apply Photo Filters and Adjust Colors",
        description:
          "Follow these simple steps to convert a color image to black and white, fix lighting, or apply creative moods to your picture.",
      },
      benefits: {
        title: "Why Use Our Online Photo Color Changer?",
        description:
          "Achieve professional color grading and high-quality photo effects instantly without downloading heavy or expensive editing software.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Learn more about adjusting image saturation, using our free photo filters, and correctly making pictures black and white.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        title: "Professional Color Grading — Right in Your Browser",
        level: "h2",
        paragraphs: [
          "When searching for a photo filter online, users often find tools that degrade their image quality or lock the best features behind a paywall. Our platform provides a professional-grade workbench where you can edit color image online with zero restrictions. Whether your goal is to quickly make image black and white for a dramatic portfolio piece, or to subtly adjust photo brightness and contrast online, you have complete creative control over the final look.",
          "Our tool acts as a comprehensive online photo color changer. You do not need to be an expert in color theory to get great results. You can choose from our library of curated mood presets to instantly transform the atmosphere of your shot, or dive into the manual adjustment panel to tweak exposure, temperature, and vibrance by hand. It is the fastest way to get color graded photos without a subscription.",
        ],
        note: "All edits are processed securely in your browser. We never add watermarks or compress your original image resolution.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Available Editing Controls",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Lighting Adjustments",
                icon: "Sun",
                description:
                  "Fix poorly lit photos by adjusting brightness, contrast, and exposure sliders independently.",
                accent: "primary",
              },
              {
                label: "Color & Tone Corrections",
                icon: "Palette",
                description:
                  "Use the image hue changer, saturation, temperature, and vibrance controls to make colors pop.",
                accent: "primary",
              },
              {
                label: "One-Click Mood Presets",
                icon: "Film",
                description:
                  "Instantly apply Vintage, Noir, Cyberpunk, Cool, Warm, Dramatic, or Faded filters.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "text",
        title: "Make Photos Look Cinematic with Vintage & Grayscale Effects",
        level: "h2",
        paragraphs: [
          "Sometimes a standard, unedited photograph doesn't convey the right emotion. Using a vintage photo effect online can give your digital pictures the nostalgic, timeless feel of 35mm film. By selecting the 'Vintage' or 'Faded' preset, the tool instantly adjusts the tonal curve to soften the blacks and warm up the highlights.",
          "If you want to strip away color entirely, our black and white image converter is the perfect solution. Simply select the 'Noir' preset, or manually drag the saturation slider all the way down to convert to grayscale. This allows you to create high-contrast, dramatic monochromatic imagery while preserving the crisp, high-resolution details of your original upload.",
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Presets vs. Manual Adjustments",
            description:
              "Understand the different ways you can edit your photo's colors in our workbench.",
            columns: ["Method", "How It Works", "Best Used For"],
            rows: [
              {
                Method: "Mood Presets (e.g., Cyberpunk, Vintage)",
                "How It Works":
                  "Applies a predefined combination of color, contrast, and tone shifts instantly.",
                "Best Used For":
                  "Quick, stylistic transformations and achieving a specific aesthetic.",
              },
              {
                Method: "Black & White / Noir",
                "How It Works":
                  "Removes all color data, adjusting contrast to highlight shadows and light.",
                "Best Used For":
                  "Dramatic portraits, architectural photography, and fine art.",
              },
              {
                Method: "Manual Lighting Sliders",
                "How It Works":
                  "Adjusts exposure, brightness, and contrast without shifting the actual colors.",
                "Best Used For":
                  "Fixing overexposed skies or brightening dark, underexposed shadows.",
              },
              {
                Method: "Manual Color Sliders",
                "How It Works":
                  "Shifts hue, saturation, and temperature to alter the image's color profile.",
                "Best Used For":
                  "Correcting white balance or creating an online hue saturation editor effect.",
              },
            ],
            note: "You can combine methods! Apply a preset first, then fine-tune it using the manual sliders.",
            emphasisColumns: ["Method"],
            monospaceColumns: [],
          },
        ],
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload your image",
        description:
          "Drag and drop your photo onto the large workbench upload zone or click 'Drop your masterpiece' to browse. The workbench loads your image immediately with a live preview.",
      },
      {
        number: "02",
        title: "Choose a mood preset",
        description:
          "Select a mood preset (like Vintage, Noir, Cyberpunk, or Faded) for a one-click creative look. Only one preset can be active at a time.",
      },
      {
        number: "03",
        title: "Fine-tune with manual sliders",
        description:
          "Use the Adjustments panel on the right to refine brightness, contrast, saturation, hue, exposure, and temperature independently. These work alongside any preset you have selected.",
      },
      {
        number: "04",
        title: "Apply and download",
        description:
          "Click 'Apply Grade' to process the effect. The workbench replaces the preview with your graded result. Click 'Download Result' to save the color-graded JPEG to your device.",
      },
    ],
    benefits: [
      {
        icon: "Eye",
        title: "Real-Time Preview",
        description:
          "No guessing required. See exactly how your picture filters online free adjustments look on the workbench before you finalize and download the file.",
      },
      {
        icon: "Sliders",
        title: "Granular Manual Control",
        description:
          "Don't just rely on auto-filters. Our online picture editor brightness contrast tools let you dial in the exact exposure and saturation levels you need.",
      },
      {
        icon: "Maximize",
        title: "High-Resolution Output",
        description:
          "Convert color photo to black and white online free without losing resolution. We never downscale or heavily compress your masterpiece.",
      },
      {
        icon: "Film",
        title: "Professional Presets",
        description:
          "Make photos look cinematic in one click. Our curated presets like Noir, Dramatic, and Cyberpunk mimic expensive professional color grading LUTs.",
      },
    ],
    faqs: [
      {
        question:
          "How do I convert color photos to black and white in high resolution for free?",
        answer:
          "Upload your image to our workbench. You can either select the 'Noir' preset for a punchy, high-contrast black and white look, or simply drag the manual 'Saturation' slider all the way to the left to make the photo grayscale. Once applied, click download to save your high-resolution result.",
      },
      {
        question:
          "Can I adjust photo brightness and contrast without using Photoshop?",
        answer:
          "Yes. Our tool functions as an advanced online picture editor for brightness and contrast. Once you upload an image, use the sliders in the right-hand adjustment panel to lighten dark shadows, tone down overexposed highlights, and increase the contrast for a punchier image.",
      },
      {
        question:
          "How to make a photo look cinematic with free online color grading tools?",
        answer:
          "To achieve a cinematic look, upload your photo and experiment with the 'Dramatic' or 'Cyberpunk' presets. Alternatively, you can manually lower the exposure slightly, increase the contrast, and adjust the temperature slider (warm for a nostalgic film look, cool for a modern thriller aesthetic).",
      },
      {
        question:
          "Why do my photos look blurry after converting them to grayscale online?",
        answer:
          "Many free grayscale image converter tools automatically compress your files to save server space, resulting in pixelation. Our tool applies the color math directly to your image's original pixels, ensuring your downloaded black and white photo remains sharp and high quality.",
      },
      {
        question: "Can I change the hue of a transparent PNG image online?",
        answer:
          "Yes. If you upload a transparent PNG (like a logo or graphic), you can use the 'Hue' slider to shift the colors across the spectrum. The transparency will remain intact in the preview, though the final output is currently delivered as a JPEG. If you strictly need to keep the transparent background, ensure you use a dedicated PNG recolor tool.",
      },
      {
        question:
          "How to achieve a vintage 90s aesthetic using online photo effects?",
        answer:
          "Upload your image and apply the 'Vintage' or 'Faded' preset. To enhance the retro look further using manual controls, lower the contrast slightly to wash out the blacks, drop the saturation just a bit, and push the temperature slider towards the warmer (yellow/orange) side.",
      },
      {
        question:
          "What is the difference between an image hue changer and color grading?",
        answer:
          "A hue changer simply rotates all the colors in an image around the color wheel (e.g., turning reds into blues). Color grading is a more holistic process that involves balancing exposure, adjusting contrast, and selectively tweaking temperatures and tints to create a specific emotional atmosphere or 'look'.",
      },
      {
        question: "How do I fix overexposed lighting in a photo online?",
        answer:
          "While we do not have a magic 'auto-fix' button, you can easily correct overexposure manually. Upload your bright image, gently lower the 'Exposure' slider, and decrease the 'Brightness'. You may also want to slightly increase the 'Contrast' to bring back details washed out by the harsh lighting.",
      },
    ],
    useCases: [
      {
        label: "Social Media Content",
        icon: "MonitorPlay",
        description:
          "Apply a cohesive vintage or cinematic filter to all your photos to maintain a consistent, aesthetic grid.",
        accent: "primary",
      },
      {
        label: "Photography Touch-ups",
        icon: "Camera",
        description:
          "Quickly adjust photo lighting online to save underexposed shots without booting up heavy desktop software.",
        accent: "primary",
      },
      {
        label: "Graphic Design",
        icon: "Brush",
        description:
          "Recolor image online free by using the hue and saturation sliders to make stock photos match your brand's color palette.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Content Creators",
        icon: "Video",
        description:
          "Edit thumbnails and promotional images with high-contrast, dramatic filters that grab attention.",
        accent: "accent",
      },
      {
        label: "Amateur Photographers",
        icon: "Image",
        description:
          "Experiment with color grading and black and white conversions to elevate everyday snapshots into art.",
        accent: "accent",
      },
      {
        label: "Marketers & Advertisers",
        icon: "Briefcase",
        description:
          "Easily change the mood of a product photo from warm and inviting to cool and professional in seconds.",
        accent: "accent",
      },
    ],
  },
};

export default imageColorEffectsTool;
export { imageColorEffectsTool };
